import i18next from 'i18next';

// Types pour les rôles utilisateur
export type UserRole = 'user' | 'admin';

// Types utilisateur
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  partner1FirstName?: string;
  partner1LastName?: string;
  partner2FirstName?: string;
  partner2LastName?: string;
  emailVerified: boolean;
  createdAt: string;
  lastLogin?: string;
  coupleId?: string | null;
  ownCoupleId?: string;
  coupleRole?: string | null;
  hasPartner?: boolean;
  role?: UserRole;
  isBlocked?: boolean;
  effectivePlan?: PlanType;
}

// Types d'authentification
export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  invitationToken?: string;
  joinEmail?: string;
  prelaunchToken?: string;
  referralSource?: string;
  referralSourceOther?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

// Types pour la vérification email
export interface VerifyEmailData {
  token: string;
}

export interface ResendVerificationData {
  email: string;
}

// Types pour les invitations partenaire
export interface PartnerInvitation {
  id: string;
  senderEmail: string;
  receiverEmail: string;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  expiresAt: string;
  createdAt: string;
}

export interface JoinRequest {
  id: string;
  senderEmail: string;
  senderFirstName?: string;
  senderLastName?: string;
  status: 'pending' | 'accepted' | 'rejected';
  createdAt: string;
}

// Types catégories
export interface Subcategory {
  id: string;
  name: string;
  icon: string | null;
  isDefault: boolean;
}

export interface Category {
  id: string;
  name: string;
  icon: string | null;
  isDefault: boolean;
  subcategories: Subcategory[];
}

// Types revenus (nouveau format mensuel)
export interface Income {
  id: string;
  partner: 'partner1' | 'partner2';
  amount: number;
  month: number;
  year: number;
  isRecurring: boolean;
  isActive: boolean;
  customLabel?: string;
  category: { id: string; name: string; icon: string | null } | null;
  subcategory: { id: string; name: string; icon: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateIncomeData {
  categoryId?: string;
  subcategoryId?: string;
  partner: 'partner1' | 'partner2';
  customLabel?: string;
  amount: number;
  month: number;
  year: number;
  isRecurring?: boolean;
}

// Types dépenses (nouveau format mensuel)
export interface ExpenseSplit {
  partner1Share: number;
  partner2Share: number;
  partner1Percentage: number;
  partner2Percentage: number;
}

export interface Expense {
  id: string;
  amount: number;
  month: number;
  year: number;
  splitType: 'proportional' | 'equal' | 'custom' | 'partner1_only' | 'partner2_only';
  customPartner1Percentage: number | null;
  customPartner2Percentage: number | null;
  customSplitUnit: 'percentage' | 'fixed' | null;
  customFixedAmount: number | null;
  customReferencePartner: 'partner1' | 'partner2' | null;
  isRecurring: boolean;
  isPersonal: boolean;
  paidBy?: 'partner1' | 'partner2';
  isActive: boolean;
  customLabel?: string;
  category: { id: string; name: string; icon: string | null } | null;
  subcategory: { id: string; name: string; icon: string | null } | null;
  split: ExpenseSplit;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExpenseData {
  categoryId?: string;
  subcategoryId?: string;
  customLabel?: string;
  amount: number;
  month: number;
  year: number;
  splitType?: 'proportional' | 'equal' | 'custom' | 'partner1_only' | 'partner2_only';
  customPartner1Percentage?: number;
  customPartner2Percentage?: number;
  customSplitUnit?: 'percentage' | 'fixed';
  customFixedAmount?: number;
  customReferencePartner?: 'partner1' | 'partner2';
  isRecurring?: boolean;
  isPersonal?: boolean;
  paidBy?: 'partner1' | 'partner2';
}

// Types revenus personnels (appartient à l'utilisateur, pas au couple)
export interface PersonalIncome {
  id: string;
  amount: number;
  month: number;
  year: number;
  isRecurring: boolean;
  isActive: boolean;
  customLabel?: string;
  category: { id: string; name: string; icon: string | null } | null;
  subcategory: { id: string; name: string; icon: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePersonalIncomeData {
  categoryId?: string;
  subcategoryId?: string;
  customLabel?: string;
  amount: number;
  month: number;
  year: number;
  isRecurring?: boolean;
}

// Types dépenses personnelles (appartient à l'utilisateur, pas au couple)
export interface PersonalExpense {
  id: string;
  amount: number;
  month: number;
  year: number;
  isRecurring: boolean;
  isActive: boolean;
  customLabel?: string;
  category: { id: string; name: string; icon: string | null } | null;
  subcategory: { id: string; name: string; icon: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePersonalExpenseData {
  categoryId?: string;
  subcategoryId?: string;
  customLabel?: string;
  amount: number;
  month: number;
  year: number;
  isRecurring?: boolean;
}

// Types comptes bancaires
export interface BankAccount {
  id: string;
  name: string;
  color: string;
  isDefault: boolean;
  startDate: string | null;
  startingBalance: number;
}

// Types comptes bancaires partagés
export interface SharedBankAccount {
  id: string;
  name: string;
  color: string;
  isDefault: boolean;
  startDate: string | null;
  startingBalance: number;
}

// Types paramètres
export type AccountType = 'joint_with_personal' | 'joint_only' | 'personal_with_transfers';
export type LivingType = 'couple' | 'colocation';

export interface Settings {
  livingType: LivingType;
  partner1Percentage: number;
  partner2Percentage: number;
  calculationMode: 'proportional' | 'equal' | 'fixed' | 'custom';
  accountType: AccountType;
  // Compte commun - date de début et solde initial
  jointAccountStartDate: string | null;
  jointAccountStartingBalance: number | null;
  // Mon budget (personnel) - date de début et solde initial
  personalBudgetStartDate: string | null;
  personalBudgetStartingBalance: number | null;
  // Partenaire de référence pour la répartition personnalisée
  customReferencePartner: 'partner1' | 'partner2';
  // Revenu mensuel manuel du partenaire 2 (si pas de second utilisateur dans le couple)
  partner2ManualMonthlyIncome: number | null;
}

// Types résumé budget
export interface SharedBankAccountSummary {
  id: string;
  name: string;
  color: string;
  startDate: string | null;
  totalIncome: number;
  totalCredits: number;
  totalExpenses: number;
  balance: number;
  endingBalance: number;
  partner1Share: number;
  partner2Share: number;
  partner1Percentage: number;
  partner2Percentage: number;
  partner1Contributions: number;
  partner2Contributions: number;
  partner1CumulativeBalance: number;
  partner2CumulativeBalance: number;
}

export interface BudgetSummary {
  totalIncome: number;
  totalCredits?: number;
  partner1Income: number;
  partner2Income: number;
  totalExpenses: number;
  partner1Share: number;
  partner2Share: number;
  partner1Percentage: number;
  partner2Percentage: number;
  balance: number; // Balance de la période (revenus - dépenses)
  endingBalance?: number; // Solde cumulé (incluant les mois précédents)
  // Suivi du solde par partenaire
  partner1Contributions?: number; // Virements sur le compte commun (période courante)
  partner2Contributions?: number;
  partner1CumulativeBalance?: number; // Solde cumulatif (contributions - part dépenses)
  partner2CumulativeBalance?: number;
  unattributedCreditsCount?: number; // Crédits sans attribution partenaire
  unattributedCreditsAmount?: number;
  accounts?: SharedBankAccountSummary[];
}

export interface MonthlySummary {
  total: number;
  partner1: number;
  partner2: number;
}

// Types API
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Array<{ field: string; message: string }>;
}

// Types totaux
export interface IncomeTotals {
  total: number;
  partner1: number;
  partner2: number;
}

export interface ExpenseTotals {
  total: number;
  partner1: number;
  partner2: number;
}

// Types pour le résumé du budget personnel par compte bancaire
export interface BankAccountSummary {
  id: string;
  name: string;
  color: string;
  totalIncome: number;
  totalExpenses: number;
  balance: number; // Balance de la période (revenus - dépenses)
  startingBalance: number;
  currentBalance: number; // startingBalance + balance
  endingBalance?: number; // Solde cumulé (incluant les mois précédents)
}

export interface PersonalBudgetSummary {
  totalIncome: number;
  totalExpenses: number;
  balance: number; // Balance de la période (revenus - dépenses)
  endingBalance?: number; // Solde cumulé global (incluant les mois précédents)
  accounts: BankAccountSummary[];
}

// Types pour les endpoints unifiés du dashboard
export interface FullDashboardData {
  summary: BudgetSummary;
  stats: DashboardStats;
}

export interface FullPersonalDashboardData {
  summary: PersonalBudgetSummary;
  stats: PersonalDashboardStats;
}

// Types pour les statistiques avancées du dashboard
export interface CategoryBreakdown {
  categoryId: string;
  categoryName: string;
  categoryIcon: string | null;
  categoryIsDefault: boolean;
  amount: number;
  percentage: number;
  color: string;
}

export interface SubcategoryBreakdown {
  subcategoryId: string;
  subcategoryName: string;
  subcategoryIcon: string | null;
  amount: number;
  percentage: number;
  color: string;
}

export interface MonthlyTrend {
  month: number;
  year: number;
  label: string;
  totalIncome: number;
  totalExpenses: number;
  balance: number;
}

export interface RecentTransaction {
  id: string;
  type: 'income' | 'expense';
  amount: number;
  date: string;
  categoryId: string | null;
  categoryName: string;
  categoryIcon: string | null;
  categoryIsDefault: boolean;
  customLabel: string | null;
  isRecurring: boolean;
  sharedBankAccountName: string | null;
  sharedBankAccountColor: string | null;
}

export interface MonthComparison {
  income: {
    current: number;
    previous: number;
    change: number;
    changePercent: number;
  };
  expenses: {
    current: number;
    previous: number;
    change: number;
    changePercent: number;
  };
  balance: {
    current: number;
    previous: number;
    change: number;
    changePercent: number;
  };
}

export interface SavingsRate {
  rate: number;
  amount: number;
  status: 'excellent' | 'good' | 'average' | 'low' | 'negative';
}

export interface RecurringForecast {
  expectedIncome: number;
  expectedExpenses: number;
  actualIncome: number;
  actualExpenses: number;
  remainingIncome: number;
  remainingExpenses: number;
  progressPercent: number;
}

export interface DashboardStats {
  categoryBreakdown: CategoryBreakdown[];
  categoryBreakdownByAccount: AccountCategoryBreakdown[];
  monthlyTrend: MonthlyTrend[];
  monthlyTrendByAccount: AccountMonthlyTrend[];
  recentTransactions: RecentTransaction[];
  monthComparison: MonthComparison;
  savingsRate: SavingsRate;
  recurringForecast: RecurringForecast;
  topCategories: CategoryBreakdown[];
}

// Types pour les statistiques du dashboard personnel
export interface PersonalRecentTransaction {
  id: string;
  type: 'income' | 'expense';
  amount: number;
  date: string;
  categoryId: string | null;
  categoryName: string;
  categoryIcon: string | null;
  categoryIsDefault: boolean;
  customLabel: string | null;
  isRecurring: boolean;
  bankAccountName: string | null;
  bankAccountColor: string | null;
}

export interface AccountCategoryBreakdown {
  accountId: string;
  accountName: string;
  accountColor: string;
  categories: CategoryBreakdown[];
}

export interface AccountMonthlyTrend {
  accountId: string;
  accountName: string;
  accountColor: string;
  trends: MonthlyTrend[];
}

// Bilan partenaire pour le mode personal_with_transfers
export interface PersonalPartnerBalance {
  userSharedExpenses: number;
  userShareOfOwnExpenses: number;
  partnerShareOfUserExpenses: number;
  partnerSharedExpenses: number;
  partnerShareOfOwnExpenses: number;
  userShareOfPartnerExpenses: number;
  netBalance: number;
  isPartnerLinked: boolean;
}

export interface PersonalDashboardStats {
  categoryBreakdown: CategoryBreakdown[];
  categoryBreakdownByAccount: AccountCategoryBreakdown[];
  monthlyTrend: MonthlyTrend[];
  monthlyTrendByAccount: AccountMonthlyTrend[];
  recentTransactions: PersonalRecentTransaction[];
  monthComparison: MonthComparison;
  savingsRate: SavingsRate;
  recurringForecast: RecurringForecast;
  topCategories: CategoryBreakdown[];
  partnerBalance?: PersonalPartnerBalance;
}

// ============================================
// Types pour l'administration
// ============================================

// Configuration publique de l'application
export interface PublicConfig {
  registrationEnabled: boolean;
  paymentsEnabled: boolean;
}

// ============================================
// Bandeau d'annonce
// ============================================

export type AnnouncementLevel = 'success' | 'info' | 'warning' | 'danger';

export interface Announcement {
  id: string;
  /** Change à chaque modification du contenu : un bandeau réédité réapparaît. */
  version: string;
  /** Au plus une annonce active à la fois — c'est celle que le bandeau affiche. */
  isActive: boolean;
  level: AnnouncementLevel;
  contentFr: string;
  contentEn: string;
  publishedAt: string;
  updatedAt: string;
}

/** Page d'historique des annonces. */
export interface AnnouncementPage {
  items: Announcement[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Champ déclaré par un modèle d'annonce.
 *
 * `text` est une valeur libre nommée (`{{text:ville}}`). `period` désigne une
 * partie de l'unique période de l'annonce (`{{debut:date}}`, `{{fin:heure}}`…) :
 * début et fin partagent une seule saisie, plutôt que de redemander la date
 * pour chaque horaire.
 */
export type TemplateVariable =
  | { kind: 'text'; name: string }
  | { kind: 'period'; moment: 'debut' | 'fin'; part: 'date' | 'heure' };

/** Valeurs saisies pour instancier un modèle. */
export interface TemplateValues {
  /** Valeurs des `{{text:…}}`, par nom. */
  texts: Record<string, string>;
  /** `YYYY-MM-DD` — sert aussi d'ancrage aux heures (décalage saisonnier). */
  debutDate: string;
  /** `HH:mm`, en heure de Paris. */
  debutHeure: string;
  /** Vide = la période se termine le jour même. */
  finDate: string;
  finHeure: string;
}

export interface AnnouncementTemplate {
  id: string;
  name: string;
  level: AnnouncementLevel;
  contentFr: string;
  contentEn: string;
  variables: TemplateVariable[];
  createdAt: string;
  updatedAt: string;
}

export interface AnnouncementAdminData {
  announcements: AnnouncementPage;
  templates: AnnouncementTemplate[];
  /** `false` si aucune clé DeepL n'est configurée : le bouton « Traduire » est masqué. */
  translationEnabled: boolean;
}

// Statistiques admin
export interface ReferralStat {
  source: string;
  count: number;
}

export interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  blockedUsers: number;
  pendingSubscriptions: number;
  invitedSubscriptions: number;
  registeredSubscriptions: number;
  registrationEnabled: boolean;
  paymentsEnabled: boolean;
  referralStats: ReferralStat[];
  premiumUsers?: number;
  duoUsers?: number;
  trialingUsers?: number;
  monthlyRevenue?: number;
}

// Utilisateur admin (vue détaillée)
export interface AdminUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: UserRole;
  isBlocked: boolean;
  emailVerified: boolean;
  createdAt: string;
  lastLogin: string | null;
  coupleId: string | null;
  referralSource: string | null;
  grantedPlan?: PlanType | null;
  effectivePlan?: PlanType;
  subscriptionPlan?: string | null;
  subscriptionStatus?: string | null;
}

// Pré-inscription admin
export interface PrelaunchSubscription {
  id: string;
  email: string;
  status: 'pending' | 'invited' | 'registered' | 'rejected';
  createdAt: string;
  invitationSentAt: string | null;
  invitationExpiresAt: string | null;
  registeredAt: string | null;
  followUpSentAt: string | null;
  followUpResponse: 'interested' | 'not_interested' | null;
  // Historique : une demande rejetée, expirée ou dont le compte a été supprimé
  // revient en file d'attente quand la personne redemande une place
  previousStatus: string | null;
  resubmittedAt: string | null;
  rejectedAt: string | null;
  requestCount: number;
  lastRequestAt: string | null;
}

// Résultat d'envoi d'invitations
export interface SendInvitationsResult {
  success: string[];
  failed: string[];
}

// ============================================
// Types pour les widgets du dashboard
// ============================================

// Identifiants des widgets pour le compte commun
export type CommonWidgetId =
  | 'totalIncome'
  | 'totalExpenses'
  | 'balance'
  | 'savingsRate'
  | 'topCategories'
  | 'recurringForecast'
  | 'budgetProgress'
  | 'monthlyTrend'
  | 'categoryBreakdown'
  | 'recentTransactions'
  | 'partnerDetails';

// Identifiants des widgets pour le budget personnel
export type PersonalWidgetId =
  | 'totalIncome'
  | 'totalExpenses'
  | 'balance'
  | 'savingsRate'
  | 'topCategories'
  | 'recurringForecast'
  | 'budgetProgress'
  | 'monthlyTrend'
  | 'categoryBreakdown'
  | 'recentTransactions'
  | 'accountDistribution'
  | 'accountDetails'
  | 'personalPartnerDetails';

// Configuration des widgets du dashboard
export interface DashboardWidgetsConfig {
  common: CommonWidgetId[];
  personal: PersonalWidgetId[];
}

// Tailles des widgets pour la disposition fluide
export type WidgetSize = 'small' | 'medium' | 'large';

// Définition d'un widget (pour l'affichage dans la configuration)
export interface WidgetDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  size: WidgetSize;
}

// Widgets disponibles pour le compte commun (name/description sont des clés i18n dashboard.widgetNames.*)
export const COMMON_WIDGETS: WidgetDefinition[] = [
  { id: 'totalIncome', name: 'totalIncome', description: 'totalIncomeDesc', icon: 'Wallet', size: 'small' },
  { id: 'totalExpenses', name: 'totalExpenses', description: 'totalExpensesDesc', icon: 'Receipt', size: 'small' },
  { id: 'balance', name: 'balance', description: 'balanceDesc', icon: 'TrendingUp', size: 'small' },
  { id: 'savingsRate', name: 'savingsRate', description: 'savingsRateDesc', icon: 'Percent', size: 'small' },
  { id: 'topCategories', name: 'topCategories', description: 'topCategoriesDesc', icon: 'Medal', size: 'small' },
  { id: 'recurringForecast', name: 'recurringForecast', description: 'recurringForecastDesc', icon: 'Calendar', size: 'small' },
  { id: 'budgetProgress', name: 'budgetProgress', description: 'budgetProgressDesc', icon: 'Target', size: 'medium' },
  { id: 'monthlyTrend', name: 'monthlyTrend', description: 'monthlyTrendDesc', icon: 'LineChart', size: 'medium' },
  { id: 'categoryBreakdown', name: 'categoryBreakdown', description: 'categoryBreakdownDesc', icon: 'PieChart', size: 'medium' },
  { id: 'recentTransactions', name: 'recentTransactions', description: 'recentTransactionsDesc', icon: 'List', size: 'medium' },
  { id: 'partnerDetails', name: 'partnerDetails', description: 'partnerDetailsDesc', icon: 'Users', size: 'medium' },
];

// Widgets disponibles pour le budget personnel
export const PERSONAL_WIDGETS: WidgetDefinition[] = [
  { id: 'totalIncome', name: 'totalIncome', description: 'totalIncomeDesc', icon: 'Wallet', size: 'small' },
  { id: 'totalExpenses', name: 'totalExpenses', description: 'totalExpensesDesc', icon: 'Receipt', size: 'small' },
  { id: 'balance', name: 'balance', description: 'balanceDesc', icon: 'TrendingUp', size: 'small' },
  { id: 'savingsRate', name: 'savingsRate', description: 'savingsRateDesc', icon: 'Percent', size: 'small' },
  { id: 'topCategories', name: 'topCategories', description: 'topCategoriesDesc', icon: 'Medal', size: 'small' },
  { id: 'recurringForecast', name: 'recurringForecast', description: 'recurringForecastDesc', icon: 'Calendar', size: 'small' },
  { id: 'budgetProgress', name: 'budgetProgress', description: 'budgetProgressDesc', icon: 'Target', size: 'medium' },
  { id: 'monthlyTrend', name: 'monthlyTrend', description: 'monthlyTrendDesc', icon: 'LineChart', size: 'medium' },
  { id: 'categoryBreakdown', name: 'categoryBreakdown', description: 'categoryBreakdownDesc', icon: 'PieChart', size: 'medium' },
  { id: 'recentTransactions', name: 'recentTransactions', description: 'recentTransactionsDesc', icon: 'List', size: 'medium' },
  { id: 'accountDistribution', name: 'accountDistribution', description: 'accountDistributionDesc', icon: 'PieChart', size: 'medium' },
  { id: 'accountDetails', name: 'accountDetails', description: 'accountDetailsDesc', icon: 'CreditCard', size: 'medium' },
  { id: 'personalPartnerDetails', name: 'personalPartnerDetails', description: 'personalPartnerDetailsDesc', icon: 'Users', size: 'medium' },
];

// Helper pour obtenir la taille d'un widget
export const getWidgetSize = (widgetId: string, isCommon: boolean): WidgetSize => {
  const widgets = isCommon ? COMMON_WIDGETS : PERSONAL_WIDGETS;
  const widget = widgets.find(w => w.id === widgetId);
  return widget?.size || 'small';
};

// Widgets par défaut
export const DEFAULT_COMMON_WIDGETS: CommonWidgetId[] = [
  'totalIncome',
  'totalExpenses',
  'balance',
  'partnerDetails',
  'categoryBreakdown',
];

export const DEFAULT_PERSONAL_WIDGETS: PersonalWidgetId[] = [
  'totalIncome',
  'totalExpenses',
  'balance',
  'accountDistribution',
  'categoryBreakdown',
  'accountDetails',
];

// ============================================
// Types pour l'archivage (RGPD)
// ============================================

// Type d'entité archivable
export type ArchiveEntityType = 'bank_account' | 'personal_operation' | 'shared_operation' | 'user';

// Archive de compte bancaire
export interface ArchivedBankAccount {
  id: string;
  originalId: string;
  name: string;
  color: string;
  isDefault: boolean;
  startDate: string | null;
  startingBalance: number | null;
  archivedAt: string;
  expiresAt: string;
  daysRemaining: number;
  operationsCount: number;
  canRestore: boolean;
}

// Archive d'opération personnelle
export interface ArchivedPersonalOperation {
  id: string;
  originalId: string;
  operationType: string;
  amount: number;
  operationDate: string;
  customLabel: string | null;
  category: { name: string; icon: string | null } | null;
  subcategory: { name: string; icon: string | null } | null;
  bankAccount: { name: string; color: string } | null;
  archivedAt: string;
  expiresAt: string;
  daysRemaining: number;
  archivedWithAccount: boolean;
  canRestore: boolean;
}

// Archive de compte partagé
export interface ArchivedSharedBankAccount {
  id: string;
  originalId: string;
  name: string;
  color: string;
  isDefault: boolean;
  startDate: string | null;
  startingBalance: number | null;
  archivedAt: string;
  expiresAt: string;
  daysRemaining: number;
  operationsCount: number;
  canRestore: boolean;
}

// Archive d'opération partagée
export interface ArchivedSharedOperation {
  id: string;
  originalId: string;
  operationType: string;
  amount: number;
  operationDate: string;
  customLabel: string | null;
  category: { name: string; icon: string | null } | null;
  subcategory: { name: string; icon: string | null } | null;
  splitType: string;
  archivedAt: string;
  expiresAt: string;
  daysRemaining: number;
  canRestore: boolean;
  isInternalTransfer: boolean;
}

// Archive de budget prévisionnel
export interface ArchivedBudget {
  id: string;
  originalId: string;
  name: string | null;
  amount: number;
  period: string;
  startDate: string;
  endDate: string | null;
  category: { name: string; icon: string | null } | null;
  subcategory: { name: string } | null;
  archivedAt: string;
  expiresAt: string;
  daysRemaining: number;
  canRestore: boolean;
}

export interface ArchivedEnvelope {
  id: string;
  originalId: string;
  name: string;
  targetAmount: number;
  targetDate: string;
  status: string;
  archivedAt: string;
  expiresAt: string;
  daysRemaining: number;
  canRestore: boolean;
  reattachableOperationsCount: number;
}

// Nature d'une catégorie archivée
export type ArchivedCategoryKind =
  | 'income_category'
  | 'expense_category'
  | 'income_subcategory'
  | 'expense_subcategory';

// Archive de catégorie / sous-catégorie
export interface ArchivedCategory {
  id: string;
  originalId: string;
  categoryKind: ArchivedCategoryKind;
  name: string;
  icon: string | null;
  subcategoriesCount: number;
  operationsCount: number;
  budgetsCount: number;
  reattachableOperationsCount: number;
  archivedAt: string;
  expiresAt: string;
  daysRemaining: number;
  canRestore: boolean;
}

// Archive de changement de revenu du partenaire 2
export interface ArchivedPartner2IncomeChange {
  id: string;
  originalId: string;
  effectiveMonth: number;
  effectiveYear: number;
  amount: number;
  isOneOff: boolean;
  label: string | null;
  archivedAt: string;
  expiresAt: string;
  daysRemaining: number;
  canRestore: boolean;
}

// Statistiques d'archives
export interface ArchiveStats {
  totalArchived: number;
  byType: {
    bankAccounts: number;
    sharedBankAccounts: number;
    personalOperations: number;
    sharedOperations: number;
    budgets: number;
    envelopes: number;
    categories: number;
    partner2Incomes: number;
  };
  expiringWithin7Days: number;
  oldestArchive: string | null;
  newestArchive: string | null;
}

// Données complètes des archives
export interface ArchivesData {
  bankAccounts: ArchivedBankAccount[];
  personalOperations: ArchivedPersonalOperation[];
  sharedOperations: ArchivedSharedOperation[];
  sharedBankAccounts: ArchivedSharedBankAccount[];
  budgets: ArchivedBudget[];
  envelopes: ArchivedEnvelope[];
  categories: ArchivedCategory[];
  partner2Incomes: ArchivedPartner2IncomeChange[];
  stats: ArchiveStats;
}

// Résultat de restauration
export interface RestoreResult {
  success: boolean;
  message: string;
  restoredId: string;
  relatedRestored?: number;
  warnings?: string[];
}

// ============================================
// Types pour la récurrence avancée
// ============================================

/**
 * Types de fréquence de récurrence
 */
export type RecurrenceFrequency =
  | 'MONTHLY_DAY'      // Mensuel à date fixe (comportement par défaut)
  | 'MONTHLY_WEEKDAY'  // Mensuel le Nième jour de la semaine (ex: 2ème samedi)
  | 'WEEKLY'           // Hebdomadaire
  | 'EVERY_N_WEEKS'    // Toutes les N semaines
  | 'EVERY_N_MONTHS'   // Tous les N mois
  | 'YEARLY'           // Annuel (même date chaque année)
  | 'INSTALLMENTS';    // Paiements échelonnés (créés immédiatement)

/**
 * Configuration de récurrence pour une opération
 */
export interface RecurrenceConfig {
  frequency: RecurrenceFrequency;
  // Pour MONTHLY_DAY (optionnel, calculé depuis la date si absent)
  day?: number;
  // Pour MONTHLY_WEEKDAY
  weekday?: number;        // 0-6 (Lundi-Dimanche)
  weekdayOrdinal?: number; // 1-5 (1er, 2e, 3e, 4e, dernier)
  // Pour WEEKLY / EVERY_N_WEEKS
  interval?: number;       // 1=hebdo, 2=bi-hebdo, etc.
  // Pour INSTALLMENTS
  totalCount?: number;     // Nombre total de paiements
  currentCount?: number;   // Numéro de l'occurrence actuelle (lecture seule)
  // Options de fin
  endDate?: string | null; // YYYY-MM-DD
  // Pauses de prélèvement : fenêtres [start, end] sautées par la génération récurrente.
  // Une occurrence unique ignorée = fenêtre où start === end.
  pauses?: PauseWindow[] | null;
}

/** Fenêtre de pause de prélèvement (bornes incluses, dates YYYY-MM-DD). */
export interface PauseWindow {
  start: string;
  end: string;
}

/**
 * Keys pour les jours de la semaine (utiliser avec i18next)
 */
const WEEKDAY_FULL_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const MONTH_FULL_KEYS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

/**
 * Génère un label lisible pour une configuration de récurrence
 * Nécessite i18next initialisé
 */
export function getRecurrenceLabelFromConfig(
  config: RecurrenceConfig | null | undefined,
  operationDate?: string
): string {
  if (!config) return '';

  const t = (key: string, options?: Record<string, unknown>) => i18next.t(key, { ns: 'common', ...options });

  switch (config.frequency) {
    case 'MONTHLY_DAY': {
      const day = config.day || (operationDate ? new Date(operationDate).getUTCDate() : 1);
      return t('recurrence.monthlyDay', { day });
    }
    case 'MONTHLY_WEEKDAY': {
      const ordinalLabel = t(`ordinals.${config.weekdayOrdinal || 1}`);
      const weekday = t(`weekdays.${WEEKDAY_FULL_KEYS[config.weekday || 0]}`);
      return t('recurrence.monthlyWeekday', { ordinalLabel, weekday });
    }
    case 'WEEKLY': {
      const weekday = t(`weekdays.${WEEKDAY_FULL_KEYS[config.weekday || 0]}`);
      return t('recurrence.weeklyEvery', { weekday });
    }
    case 'EVERY_N_WEEKS': {
      const interval = config.interval || 2;
      return `${t('recurrence.everyPrefix')} ${interval} ${t('recurrence.weeksSuffix')}`;
    }
    case 'EVERY_N_MONTHS': {
      const monthInterval = config.interval || 2;
      return `${t('recurrence.everyPrefixMasc')} ${monthInterval} ${t('recurrence.monthsSuffix')}`;
    }
    case 'YEARLY': {
      const yearDay = config.day || (operationDate ? new Date(operationDate).getUTCDate() : 1);
      const yearMonth = operationDate ? new Date(operationDate).getUTCMonth() : 0;
      const month = t(`months.${MONTH_FULL_KEYS[yearMonth]}`);
      return t('recurrence.yearlyOn', { day: yearDay, month });
    }
    case 'INSTALLMENTS': {
      const current = config.currentCount || 1;
      const total = config.totalCount || 1;
      return `${current}/${total}`;
    }
    default:
      return '';
  }
}

// ============================================
// Types pour les budgets prévisionnels
// ============================================

export type BudgetPeriod = 'weekly' | 'monthly';
export type BudgetStatus = 'on_track' | 'warning' | 'exceeded';

/**
 * Progression d'une semaine pour un budget avec weeklyAmounts
 */
export interface WeeklyProgressItem {
  week: number;
  weekLabel: string; // "Sem. 1 (01-07/02)"
  amount: number;
  spent: number;
  remaining: number;
  progressPercent: number;
  status: BudgetStatus;
}

/**
 * Budget avec sa progression calculée
 */
export interface BudgetWithProgress {
  id: string;
  // Sélection simple (rétro-compatibilité)
  expenseCategoryId: string | null;
  expenseSubcategoryId: string | null;
  // Multi-sélection
  expenseCategoryIds: string[] | null;
  expenseSubcategoryIds: string[] | null;
  categoryName: string;
  categoryIcon: string | null;
  categoryIsDefault: boolean;
  subcategoryName: string | null;
  subcategoryIsDefault: boolean;
  name: string;
  amount: number;
  period: BudgetPeriod;
  weeklyAmounts: Record<number, number> | null;
  bankAccountIds: string[] | null;
  startDate: string;
  endDate: string | null;
  isActive: boolean;
  spent: number;
  remaining: number;
  progressPercent: number;
  status: BudgetStatus;
  periodLabel: string;
  // Progression par semaine (si weeklyAmounts défini)
  weeklyProgress?: WeeklyProgressItem[];
}

/**
 * Résumé des budgets pour le dashboard
 */
export interface BudgetProgressSummary {
  totalBudgeted: number;
  totalSpent: number;
  totalRemaining: number;
  budgets: BudgetWithProgress[];
}

// ============================================
// Types pour l'impact budget sur solde prévisionnel
// ============================================

/**
 * Détail hebdomadaire de l'impact budget
 */
export interface WeeklyForecastImpact {
  week: number;
  weekLabel: string;
  budgetAmount: number;
  spent: number;
  impact: number;
  status: 'past' | 'current' | 'future';
}

/**
 * Impact d'un budget individuel sur le solde prévisionnel
 */
export interface BudgetForecastImpact {
  budgetId: string;
  budgetName: string;
  categoryName: string;
  period: BudgetPeriod;
  budgetAmount: number;
  alreadySpent: number;
  forecastImpact: number;
  weeklyDetails?: WeeklyForecastImpact[];
}

/**
 * Résultat global du calcul d'impact budget
 */
export interface BudgetForecastResult {
  totalBudgetImpact: number;
  budgets: BudgetForecastImpact[];
  hasBudgets: boolean;
}

/**
 * DTO pour créer un budget
 */
export interface CreateBudgetDto {
  // Sélection simple (rétro-compatibilité)
  expenseCategoryId?: string;
  expenseSubcategoryId?: string;
  // Multi-sélection (prioritaire si défini)
  expenseCategoryIds?: string[];
  expenseSubcategoryIds?: string[];
  name?: string;
  amount: number;
  period: BudgetPeriod;
  weeklyAmounts?: Record<number, number> | null;
  bankAccountIds?: string[] | null;
  startDate: string;
  endDate?: string | null;
}

/**
 * DTO pour mettre à jour un budget
 */
export interface UpdateBudgetDto extends Partial<CreateBudgetDto> {
  isActive?: boolean;
}

// ============================================
// Types pour la monétisation (Abonnements Stripe)
// ============================================

export type PlanType = 'free' | 'premium' | 'duo';
export type BillingCycle = 'monthly' | 'yearly';
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'paused' | 'cancelled' | 'expired';

export interface SubscriptionDto {
  id: string;
  plan: PlanType;
  billingCycle: BillingCycle | null;
  status: SubscriptionStatus;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEndDate: string | null;
  pendingPlan: PlanType | null;
  pendingBillingCycle: BillingCycle | null;
}

export interface PlanLimits {
  personalAccounts: number;
  sharedAccounts: number;
  budgets: number;
  envelopes: number;
  customCategories: number;
  historyMonths: number;
  canExport: boolean;
  dashboardFull: boolean;
}

export interface PlanUsage {
  personalAccounts: number;
  sharedAccounts: number;
  budgets: number;
  envelopes: number;
  customCategories: number;
}

export interface UserPlanInfo {
  effectivePlan: PlanType;
  source: 'grant' | 'subscription' | 'duo_benefit' | 'free';
  subscription: SubscriptionDto | null;
  limits: PlanLimits;
  usage: PlanUsage;
  canUpgrade: boolean;
  canDowngrade: boolean;
  lastPlanChangeAt: string | null;
  duoBenefactorName?: string;
}

export interface InvoiceDto {
  id: string;
  invoiceNumber: string;
  amountPaid: number;
  currency: string;
  status: string;
  invoicePdfUrl: string;
  hostedInvoiceUrl: string;
  periodStart: string;
  periodEnd: string;
  createdAt: string;
}

// ============================================
// Types pour les enveloppes budgétaires
// ============================================

export type EnvelopeStatus = 'active' | 'spending' | 'completed' | 'cancelled';

export interface EnvelopeWithProgress {
  id: string;
  name: string;
  targetAmount: number;
  targetDate: string;
  spendingEndDate: string | null;
  bankAccountIds: string[] | null;
  status: EnvelopeStatus;
  isActive: boolean;
  createdAt: string;
  // Phase active (épargne)
  totalContributed: number;
  monthsRemaining: number;
  monthlyContribution: number;
  currentMonthFulfilled: boolean;
  progressPercent: number;
  // Projections
  projectedTotal: number;
  projectedThisMonth: number;
  recommendedContribution: number;
  projectedProgressPercent: number;
  fullProjectedTotal: number;
  atRisk: boolean;
  // Phase spending (dépense)
  totalSpent: number;
  spendingBudget: number;
  spendingRemaining: number;
  spendingProgressPercent: number;
}

export interface EnvelopeSummary {
  envelopes: EnvelopeWithProgress[];
  totalTargetAmount: number;
  totalContributed: number;
}

export interface EnvelopeForecastImpact {
  envelopeId: string;
  envelopeName: string;
  savingsMode: 'balance' | 'operations';
  targetAmount: number;
  cumulativeSavings: number;
  savedThisMonth: number;
  recommendedContribution: number;
  isFulfilled: boolean;
}

export interface EnvelopeForecastResult {
  totalEnvelopeImpact: number;
  envelopes: EnvelopeForecastImpact[];
  hasEnvelopes: boolean;
}

export interface CreateEnvelopeDto {
  name: string;
  targetAmount: number;
  targetDate: string;
  bankAccountIds?: string[] | null;
}

export interface UpdateEnvelopeDto {
  name?: string;
  targetAmount?: number;
  targetDate?: string;
  bankAccountIds?: string[] | null;
}

export interface ConvertToSpendingDto {
  spendingEndDate?: string | null;
}
